import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Trash2 } from "lucide-react";
import { timeEntriesApi } from "../../services/api";
import { getMatterColor, formatDuration, toISODateStr } from "./timeCalendarUtils";
import Toggle from "../ui/Toggle";

// ── Props ────────────────────────────────────────────────────────────────────

interface Props {
  entry: any | null;
  matters: any[];
  clients: any[];
  onClose: () => void;
  onUpdated: () => void;
  onDeleted: () => void;
}

// ── Status badge styling ─────────────────────────────────────────────────────

const statusStyles: Record<string, { bg: string; text: string; label: string }> = {
  unbilled: { bg: "bg-emerald-100", text: "text-emerald-700", label: "Unbilled" },
  billed: { bg: "bg-slate-100", text: "text-slate-600", label: "Billed" },
  written_off: { bg: "bg-red-100", text: "text-red-600", label: "Written Off" },
};

// ── Component ────────────────────────────────────────────────────────────────

const TimeCalendarEntryDetail: React.FC<Props> = ({
  entry,
  matters,
  clients,
  onClose,
  onUpdated,
  onDeleted,
}) => {
  // ── Editable state ─────────────────────────────────────────────────────

  const [date, setDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [hours, setHours] = useState("");
  const [mins, setMins] = useState("");
  const [narrative, setNarrative] = useState("");
  const [rateCents, setRateCents] = useState(0);
  const [billable, setBillable] = useState(true);

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState("");

  // ── Sync state when entry changes ──────────────────────────────────────

  useEffect(() => {
    if (!entry) return;

    const entryDate = entry.date ? entry.date.split("T")[0] : "";
    setDate(entryDate);
    setStartTime(entry.startTime || "");
    setHours(Math.floor(entry.minutes / 60).toString());
    setMins((entry.minutes % 60).toString());
    setNarrative(entry.narrative || "");
    setRateCents(entry.rateCents || 0);
    setBillable(entry.billable ?? true);
    setConfirmDelete(false);
    setError("");
  }, [entry]);

  if (!entry) return null;

  // ── Derived values ─────────────────────────────────────────────────────

  const matterTitle =
    typeof entry.matterId === "object"
      ? entry.matterId?.title || "Untitled Matter"
      : "Matter";

  const matterIdStr =
    typeof entry.matterId === "object"
      ? entry.matterId?._id || ""
      : entry.matterId || "";

  const clientName =
    typeof entry.clientId === "object"
      ? entry.clientId?.name ||
        `${entry.clientId?.firstName || ""} ${entry.clientId?.lastName || ""}`.trim() ||
        "Unknown Client"
      : (() => {
          const client = clients.find((c: any) => c._id === entry.clientId);
          if (!client) return "Unknown Client";
          return client.name || `${client.firstName || ""} ${client.lastName || ""}`.trim();
        })();

  const status = entry.status || "unbilled";
  const isEditable = status === "unbilled";
  const matterColor = matterIdStr ? getMatterColor(matterIdStr) : null;
  const statusStyle = statusStyles[status] || statusStyles.unbilled;

  // ── Dirty check ────────────────────────────────────────────────────────

  const totalMinutes =
    (parseInt(hours) || 0) * 60 + (parseInt(mins) || 0);

  const originalDate = entry.date ? entry.date.split("T")[0] : "";

  const isDirty =
    date !== originalDate ||
    startTime !== (entry.startTime || "") ||
    totalMinutes !== entry.minutes ||
    narrative !== (entry.narrative || "") ||
    rateCents !== (entry.rateCents || 0) ||
    billable !== (entry.billable ?? true);

  // ── Save handler ───────────────────────────────────────────────────────

  const handleSave = async () => {
    setSaving(true);
    setError("");

    try {
      const updates: Record<string, any> = {};

      if (date !== originalDate) updates.date = date;
      if (startTime !== (entry.startTime || "")) updates.startTime = startTime;
      if (totalMinutes !== entry.minutes) {
        updates.minutes = totalMinutes;
        updates.amountCents = Math.round((totalMinutes / 60) * rateCents);
      }
      if (narrative !== (entry.narrative || "")) updates.narrative = narrative;
      if (rateCents !== (entry.rateCents || 0)) {
        updates.rateCents = rateCents;
        updates.amountCents = Math.round((totalMinutes / 60) * rateCents);
      }
      if (billable !== (entry.billable ?? true)) updates.billable = billable;

      await timeEntriesApi.update(entry._id, updates);
      onUpdated();
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Failed to update time entry"
      );
    } finally {
      setSaving(false);
    }
  };

  // ── Delete handler ─────────────────────────────────────────────────────

  const handleDelete = async () => {
    setDeleting(true);
    setError("");

    try {
      await timeEntriesApi.remove(entry._id);
      onDeleted();
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Failed to delete time entry"
      );
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  return (
    <AnimatePresence>
      {entry && (
        <motion.div
          key="entry-detail"
          initial={{ x: 320 }}
          animate={{ x: 0 }}
          exit={{ x: 320 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className="absolute top-0 right-0 bottom-0 w-80 bg-white border-l border-slate-200 shadow-xl z-30 flex flex-col"
        >
          {/* ── Header ──────────────────────────────────────────────── */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100">
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
            <h3 className="text-sm font-semibold text-slate-800">Time Entry</h3>
          </div>

          {/* ── Scrollable content ──────────────────────────────────── */}
          <div className="flex-1 overflow-y-auto">
            {/* Read-only info section */}
            <div className="px-4 py-3 border-b border-slate-100 space-y-2">
              {/* Matter name with color dot */}
              <div className="flex items-center gap-2">
                {matterColor && (
                  <span
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: matterColor.hex }}
                  />
                )}
                <span className="text-sm font-semibold text-slate-800 truncate">
                  {matterTitle}
                </span>
              </div>

              {/* Client name */}
              <p className="text-xs text-slate-500">
                Client: <span className="font-medium text-slate-700">{clientName}</span>
              </p>

              {/* Status badge */}
              <span
                className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${statusStyle.bg} ${statusStyle.text}`}
              >
                {statusStyle.label}
              </span>
            </div>

            {/* Editable fields */}
            <div className="px-4 py-4 space-y-4">
              {/* Date */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Date
                </label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  disabled={!isEditable}
                  className={`w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-emerald-600 ${
                    !isEditable ? "bg-slate-50 text-slate-500 cursor-not-allowed" : ""
                  }`}
                />
              </div>

              {/* Start Time */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Start Time
                </label>
                <input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  disabled={!isEditable}
                  className={`w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-emerald-600 ${
                    !isEditable ? "bg-slate-50 text-slate-500 cursor-not-allowed" : ""
                  }`}
                />
              </div>

              {/* Duration: hours + minutes */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Duration
                  {totalMinutes > 0 && (
                    <span className="text-slate-400 font-normal ml-1">
                      ({formatDuration(totalMinutes)})
                    </span>
                  )}
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <div className="relative">
                    <input
                      type="text"
                      inputMode="numeric"
                      value={hours === "0" ? "" : hours}
                      onChange={(e) => {
                        const v = e.target.value.replace(/[^0-9]/g, "");
                        setHours(v);
                      }}
                      disabled={!isEditable}
                      placeholder="0"
                      className={`w-full rounded-xl border border-slate-200 px-3 py-2 pr-8 text-sm focus:outline-none focus:border-emerald-600 ${
                        !isEditable ? "bg-slate-50 text-slate-500 cursor-not-allowed" : ""
                      }`}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">
                      h
                    </span>
                  </div>
                  <div className="relative">
                    <input
                      type="text"
                      inputMode="numeric"
                      value={mins === "0" ? "" : mins}
                      onChange={(e) => {
                        const v = e.target.value.replace(/[^0-9]/g, "");
                        const parsed = parseInt(v) || 0;
                        setMins(parsed > 59 ? "59" : v);
                      }}
                      disabled={!isEditable}
                      placeholder="0"
                      className={`w-full rounded-xl border border-slate-200 px-3 py-2 pr-8 text-sm focus:outline-none focus:border-emerald-600 ${
                        !isEditable ? "bg-slate-50 text-slate-500 cursor-not-allowed" : ""
                      }`}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">
                      m
                    </span>
                  </div>
                </div>
              </div>

              {/* Narrative */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Narrative
                </label>
                <textarea
                  value={narrative}
                  onChange={(e) => setNarrative(e.target.value)}
                  rows={4}
                  disabled={!isEditable}
                  placeholder="Describe the work performed..."
                  className={`w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-emerald-600 resize-none ${
                    !isEditable ? "bg-slate-50 text-slate-500 cursor-not-allowed" : ""
                  }`}
                />
              </div>

              {/* Rate */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Rate ($/hr)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={rateCents ? rateCents / 100 : ""}
                  onChange={(e) =>
                    setRateCents(Math.round(parseFloat(e.target.value || "0") * 100))
                  }
                  disabled={!isEditable}
                  placeholder="250.00"
                  className={`w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-emerald-600 ${
                    !isEditable ? "bg-slate-50 text-slate-500 cursor-not-allowed" : ""
                  }`}
                />
              </div>

              {/* Billable toggle */}
              <div className="flex items-center gap-2">
                <Toggle
                  checked={billable}
                  onChange={(val) => setBillable(val)}
                  disabled={!isEditable}
                  size="sm"
                />
                <span className="text-sm font-medium text-slate-700">Billable</span>
              </div>

              {/* Error message */}
              {error && (
                <p className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">
                  {error}
                </p>
              )}
            </div>
          </div>

          {/* ── Footer actions ──────────────────────────────────────── */}
          {isEditable && (
            <div className="px-4 py-3 border-t border-slate-100 space-y-2">
              {/* Save + Delete row */}
              <div className="flex items-center justify-between gap-2">
                {/* Delete */}
                {confirmDelete ? (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-red-600">Delete?</span>
                    <button
                      onClick={handleDelete}
                      disabled={deleting}
                      className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-xs font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
                    >
                      {deleting ? "..." : "Yes"}
                    </button>
                    <button
                      onClick={() => setConfirmDelete(false)}
                      className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-medium text-slate-600 hover:bg-slate-50 transition-colors"
                    >
                      No
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setConfirmDelete(true)}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-red-600 hover:bg-red-50 text-sm font-medium transition-colors"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Delete
                  </button>
                )}

                {/* Save */}
                <button
                  onClick={handleSave}
                  disabled={saving || !isDirty}
                  className="px-4 py-2 rounded-xl text-white text-sm font-bold transition-colors disabled:opacity-50"
                  style={{ backgroundColor: "#0F5132" }}
                  onMouseEnter={(e) => {
                    if (!saving && isDirty)
                      e.currentTarget.style.backgroundColor = "#146C43";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "#0F5132";
                  }}
                >
                  {saving ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default TimeCalendarEntryDetail;
